import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { RequireAuth } from './auth/RequireAuth.tsx';
import {
  APPROVAL_MINE_ROLES,
  APPROVAL_QUEUE_ROLES,
  COMMISSION_LEADERBOARD_ROLES,
  COMMISSION_REPORT_VIEW_ROLES,
  COMMISSION_SETTLEMENT_ROLES,
  CUSTOMER_READ_ROLES,
  INVITE_CODE_VIEW_ROLES,
  MODEL_PLAN_VIEW_ROLES,
  RECEIPT_VIEW_ROLES,
  ROLE_OPS_ADMIN,
  ROLE_OPS_CUSTOMER_MANAGER,
  ROLE_OPS_SUPPORT,
  SALES_COST_MANAGE_ROLES,
  SERVICE_LEVEL_MANAGE_ROLES,
  type OpsRole,
} from './auth/opsRoles.ts';
import { canAccessRoles, firstAccessiblePath } from './layout/navigation.ts';
import { ToastProvider } from './components/Toast.tsx';
import { SessionProvider } from './hooks/useSession.tsx';
import { useSession } from './hooks/useSession.tsx';
import { Shell } from './layout/Shell.tsx';
import { AccountDetail } from './pages/AccountDetail.tsx';
import { AccountsList } from './pages/AccountsList.tsx';
import { ApprovalCenterPage } from './pages/approvals/ApprovalCenterPage.tsx';
import { Audit } from './pages/Audit.tsx';
import { CockpitPage } from './pages/CockpitPage.tsx';
import { CommissionSettlementPage } from './pages/CommissionSettlementPage.tsx';
import { CronRunsPage } from './pages/CronRunsPage.tsx';
import { CrmUsagePage } from './pages/CrmUsagePage.tsx';
import { BillingStatementsPage } from './pages/BillingStatementsPage.tsx';
import { CreditSettingsPage } from './pages/CreditSettingsPage.tsx';
import { CreditGrantHistoryPage } from './pages/CreditGrantHistoryPage.tsx';
import { CreditOverduePage } from './pages/CreditOverduePage.tsx';
import { CustomerManagerPoolPage } from './pages/CustomerManagerPoolPage.tsx';
import { CustomerManagerCustomer360Page } from './pages/CustomerManagerCustomer360Page.tsx';
import { CustomerManagerRequestLogsPage } from './pages/CustomerManagerRequestLogsPage.tsx';
import { CustomerManagerWorkspacePage } from './pages/CustomerManagerWorkspacePage.tsx';
import { CommissionLeaderboardPage } from './pages/CommissionLeaderboardPage.tsx';
import { CommissionReportPage } from './pages/CommissionReportPage.tsx';
import { CustomerOwnershipPage } from './pages/CustomerOwnershipPage.tsx';
import { InviteCodesPage } from './pages/InviteCodesPage.tsx';
import { ModelPlansPage } from './pages/ModelPlansPage.tsx';
import { LeadPricingPage } from './pages/LeadPricingPage.tsx';
import { MetricsHubPage } from './pages/metrics/MetricsHubPage.tsx';
import { DefaultKeyNamespacesPage } from './pages/DefaultKeyNamespacesPage.tsx';
import { DrCheckPage } from './pages/DrCheckPage.tsx';
import { EgressLineNodePage } from './pages/EgressLineNodePage.tsx';
import { EgressLinesPage } from './pages/EgressLinesPage.tsx';
import { EgressProfilesPage } from './pages/EgressProfilesPage.tsx';
import { EmailNoticesPage } from './pages/EmailNoticesPage.tsx';
import { BalanceAlertsPage } from './pages/BalanceAlertsPage.tsx';
import { CodexCapacityAlertsPage } from './pages/CodexCapacityAlertsPage.tsx';
import { BillingAnomalies } from './pages/BillingAnomalies.tsx';
import { FeishuLoginLanding } from './pages/FeishuLoginLanding.tsx';
import { FirewallRulesetDetailPage } from './pages/FirewallRulesetDetailPage.tsx';
import { FirewallRulesetsPage } from './pages/FirewallRulesetsPage.tsx';
import { GiftCards } from './pages/GiftCards.tsx';
import { HealthPerspectivePage } from './pages/health-perspective/HealthPerspectivePage.tsx';
import { Login } from './pages/Login.tsx';
import { SetPasswordPage } from './pages/SetPasswordPage.tsx';
import { ModelDetail } from './pages/ModelDetail.tsx';
import { ModelsList } from './pages/ModelsList.tsx';
import { NamespaceDetailPage } from './pages/NamespaceDetailPage.tsx';
import { NamespacesPage } from './pages/NamespacesPage.tsx';
import { NewUpstreamWizard } from './pages/NewUpstreamWizard.tsx';
import { OpsHomePage } from './pages/OpsHomePage.tsx';
import { OpsLandingPage } from './pages/OpsLandingPage.tsx';
import { OpsPermissionsPage } from './pages/OpsPermissionsPage.tsx';
import { OpsUsersPage } from './pages/OpsUsersPage.tsx';
import { OpsRequestsPage } from './pages/OpsRequestsPage.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { PaymentProvidersPage } from './pages/PaymentProvidersPage.tsx';
import { PaymentsOrdersPage } from './pages/PaymentsOrdersPage.tsx';
import { PricingPage } from './pages/PricingPage.tsx';
import { ReportsPage } from './pages/ReportsPage.tsx';
import { ReceiptsPage } from './pages/ReceiptsPage.tsx';
import { RiskWorkbenchPage } from './pages/RiskWorkbenchPage.tsx';
import { RouterClientIdentitiesPage } from './pages/RouterClientIdentitiesPage.tsx';
import { ServiceLevelsPage } from './pages/ServiceLevelsPage.tsx';
import { SkuDetailPage } from './pages/SkuDetailPage.tsx';
import { SkuMarketPage } from './pages/SkuMarketPage.tsx';
import { SkuActivationReadinessPage } from './pages/SkuActivationReadinessPage.tsx';
import { SupportRequests } from './pages/SupportRequests.tsx';
import { SupportTickets } from './pages/SupportTickets.tsx';
import { Announcements } from './pages/Announcements.tsx';
import { QuotaCenterPage } from './pages/quotas/QuotaCenterPage.tsx';
import { GuaranteedServicePage } from './pages/GuaranteedServicePage.tsx';
import { TenantRoutingPage } from './pages/TenantRoutingPage.tsx';
import { TrafficMonitorPage } from './pages/TrafficMonitorPage.tsx';
import { UpstreamErrorPolicyPage } from './pages/UpstreamErrorPolicyPage.tsx';
import { UpstreamDetail } from './pages/UpstreamDetail.tsx';
import { UpstreamsList } from './pages/UpstreamsList.tsx';
import { VendorsPage } from './pages/VendorsPage.tsx';
import { VpsManagementPage } from './pages/VpsManagementPage.tsx';
import { WebhookEventsPage } from './pages/WebhookEventsPage.tsx';

/**
 * Top-level route table.
 *
 *   /login                  — unauthenticated
 *   /* (anything else)      — gated by RequireAuth, rendered inside Shell
 *
 * The current canonical IA starts with `/business/*`, followed by
 * entity drilldowns under `/customers`, `/finance`, `/supply`,
 * `/governance`, and `/system`. Legacy flat and old overview routes
 * stay as redirect-only compatibility shims.
 * The QueryClientProvider lives in `main.tsx` so tests can mount a fresh
 * client per render.
 */
export function App(): JSX.Element {
  const admin = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={[ROLE_OPS_ADMIN]}>{element}</RequireRouteRoles>
  );
  const support = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={[ROLE_OPS_SUPPORT]}>{element}</RequireRouteRoles>
  );
  const customerOwnership = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={[ROLE_OPS_ADMIN, ROLE_OPS_CUSTOMER_MANAGER]}>
      {element}
    </RequireRouteRoles>
  );
  const customerManagerWorkspace = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={[ROLE_OPS_ADMIN, ROLE_OPS_CUSTOMER_MANAGER]}>
      {element}
    </RequireRouteRoles>
  );
  // Customer 360 detail (#1958). Its own guard, NOT the workspace one: the
  // finance manager signs stage 2 of a credit application, so they reach
  // this one page — and only this one — while the rest of the /crm
  // workspace (pool, request logs, usage) stays customer-manager. Same
  // source as the OPS_UI_ROUTE_PERMISSIONS entry and the ops-api read gate.
  // This guard decides ENTRY only. What a session may do once inside is a
  // second, narrower decision the page makes for itself — its `canWrite` is
  // CUSTOMER_MANAGER_ROLES, so finance lands on the page with the reads and
  // no write control.
  const customer360 = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={CUSTOMER_READ_ROLES}>{element}</RequireRouteRoles>
  );
  // Approval center (epic #1030 E1-S3, #1039). Guards mirror the
  // OPS_UI_ROUTE_PERMISSIONS matrix entries — matrix, these guards, and
  // ops-api requireRoles must not drift.
  const approvalQueue = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={APPROVAL_QUEUE_ROLES}>{element}</RequireRouteRoles>
  );
  const approvalMine = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={APPROVAL_MINE_ROLES}>{element}</RequireRouteRoles>
  );
  // Receipt console (epic #1031 E2-R5, #1063). Same-source role set as
  // the OPS_UI_ROUTE_PERMISSIONS matrix entry and the nav item — the
  // page itself adapts (finance/admin get the queue, sales the list).
  const receiptsView = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={RECEIPT_VIEW_ROLES}>{element}</RequireRouteRoles>
  );
  // Lead pricing panel (epic #1032 E3-P1, #1102). Same-source role set
  // as the OPS_UI_ROUTE_PERMISSIONS matrix entry and the nav item —
  // admin + sales lead ONLY (the panel exposes procurement cost, which
  // the CM role must never see).
  const leadPricing = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={SALES_COST_MANAGE_ROLES}>{element}</RequireRouteRoles>
  );
  // Service level catalogue (#1723/#1725). Same-source role set as the
  // OPS_UI_ROUTE_PERMISSIONS matrix entry and the nav item — managing a level
  // names the routing config it anchors, so the page is admin-only even though
  // a customer manager assigns levels by name on Customer 360.
  const serviceLevels = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={SERVICE_LEVEL_MANAGE_ROLES}>{element}</RequireRouteRoles>
  );
  // Commission settlement (epic #1034 E5-M4, #1146). Same-source role set
  // as the OPS_UI_ROUTE_PERMISSIONS matrix entry and the nav item —
  // finance-manager + admin ONLY (CM/lead are denied the whole surface).
  const commissionSettlement = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={COMMISSION_SETTLEMENT_ROLES}>{element}</RequireRouteRoles>
  );
  // Commission + book-performance report (epic #1034 E5-M5, #1147). The
  // report VIEW (own statements + own book) is admin/finance/lead/CM; the
  // leaderboard is admin/finance/lead only and lives on its own page so
  // finance reaches it without the procurement-bearing lead pricing page.
  const commissionReport = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={COMMISSION_REPORT_VIEW_ROLES}>{element}</RequireRouteRoles>
  );
  const commissionLeaderboard = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={COMMISSION_LEADERBOARD_ROLES}>{element}</RequireRouteRoles>
  );
  // Salesperson invite codes (epic #1475). The route guard is the VIEW set
  // (admin/lead/CM) — the same source as the matrix entry and the nav item.
  // Minting and retiring gate INSIDE the page on the MANAGE set, and the
  // settings card on admin, so a CM reaches the page but only reads.
  const inviteCodes = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={INVITE_CODE_VIEW_ROLES}>{element}</RequireRouteRoles>
  );
  // Model plans (epic #2292 / #2297). The route guard is the VIEW set
  // (admin/lead/CM) — the same source as the matrix entry and the nav
  // item. Create/edit/archive/clone/rollback gate INSIDE the page on
  // MODEL_PLAN_MANAGE_ROLES, so a CM/lead reaches the page but only reads.
  const modelPlans = (element: JSX.Element): JSX.Element => (
    <RequireRouteRoles requiredRoles={MODEL_PLAN_VIEW_ROLES}>{element}</RequireRouteRoles>
  );

  return (
    <SessionProvider>
      <ToastProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/feishu" element={<FeishuLoginLanding />} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <Shell>
                  <Routes>
                    <Route path="/" element={<RedirectToDefault />} />
                    <Route path="/home" element={<OpsHomePage />} />
                    <Route path="/landing" element={<OpsLandingPage />} />
                    <Route
                      path="/crm"
                      element={customerManagerWorkspace(<CustomerManagerWorkspacePage />)}
                    />
                    <Route
                      path="/crm/customers/:orgId"
                      element={customer360(<CustomerManagerCustomer360Page />)}
                    />
                    <Route
                      path="/crm/requests"
                      element={customerManagerWorkspace(<CustomerManagerRequestLogsPage />)}
                    />
                    <Route
                      path="/crm/customer-pool"
                      element={customerManagerWorkspace(<CustomerManagerPoolPage />)}
                    />
                    {/* Scoped usage overview (epic #1035 E6-D4, #1068):
                        sales sessions are book-scoped server-side; the
                        payload is the sales projection for every role. */}
                    <Route
                      path="/crm/usage"
                      element={customerManagerWorkspace(<CrmUsagePage />)}
                    />
                    {/* Lead pricing panel (epic #1032 E3-P1, #1102):
                        per-SKU × brand sales costs; lead/admin only. */}
                    <Route
                      path="/crm/pricing"
                      element={leadPricing(<LeadPricingPage />)}
                    />
                    {/* Commission + book-performance report (epic #1034
                        E5-M5, #1147): CM own statements + own book; lead/
                        admin/finance see wider scope, all server-derived. */}
                    <Route
                      path="/crm/commission"
                      element={commissionReport(<CommissionReportPage />)}
                    />
                    {/* Commission leaderboard (epic #1034 E5-M5, #1147): its
                        own page so finance reaches cross-brand standings +
                        house line without the procurement-bearing pricing
                        page. admin/finance/lead — NOT customer-manager. */}
                    <Route
                      path="/crm/leaderboard"
                      element={commissionLeaderboard(<CommissionLeaderboardPage />)}
                    />
                    {/* Salesperson invite codes (epic #1475): one
                        role-adaptive page. Row scope is server-injected
                        per tier — CM own codes, lead own brand, admin
                        cross-brand. */}
                    <Route
                      path="/crm/invite-codes"
                      element={inviteCodes(<InviteCodesPage />)}
                    />
                    {/* Approval center (epic #1030 E1-S3, #1039): bare
                        /approvals routes to the role-appropriate tab. */}
                    <Route path="/approvals" element={<ApprovalsIndexRedirect />} />
                    <Route
                      path="/approvals/queue"
                      element={approvalQueue(<ApprovalCenterPage tab="queue" />)}
                    />
                    <Route
                      path="/approvals/mine"
                      element={approvalMine(<ApprovalCenterPage tab="mine" />)}
                    />
                    {/* The /ops/v2 command workspace was removed (#859) after its
                        cross-domain risk queue moved into /business/risk; the
                        customer-manager routes migrated to /crm/* (#860). The
                        legacy paths below stay as redirect shims long-term so
                        operator bookmarks and shared links keep working. */}
                    <Route path="/ops/v2" element={<RedirectTo to="/business/overview" />} />
                    <Route path="/ops/v2/customer-manager" element={<RedirectTo to="/crm" />} />
                    <Route
                      path="/ops/v2/customer-manager/customers/:orgId"
                      element={<RedirectToPrefix from="/ops/v2/customer-manager" to="/crm" />}
                    />
                    <Route
                      path="/ops/v2/customer-manager/requests"
                      element={<RedirectTo to="/crm/requests" />}
                    />
                    <Route
                      path="/ops/v2/customer-pool"
                      element={<RedirectTo to="/crm/customer-pool" />}
                    />
                    {/* Business cockpit IA (epic #997). The projection pages
                        (overview/customers/supply/risk) were retired in #1007;
                        the cockpit, SKU economics, and risk workbench replace
                        them at the same URLs so operator bookmarks keep working.
                        `/business/customers` had no standalone replacement — the
                        cockpit carries the growth funnel — so it redirects to the
                        cockpit. */}
                    <Route path="/business/overview" element={admin(<CockpitPage />)} />
                    {/* Consolidated metrics hub (epic #1643): six URL-backed
                        tabs. S5 absorbed the standalone usage/pnl/sku pages,
                        so their routes redirect into the matching tab with
                        the query string preserved. */}
                    <Route path="/business/metrics" element={admin(<MetricsHubPage />)} />
                    <Route
                      path="/business/revenue"
                      element={<RedirectToTab to="/business/metrics" tab="pnl" />}
                    />
                    <Route path="/business/customers" element={<RedirectTo to="/business/overview" />} />
                    <Route
                      path="/business/supply"
                      element={<RedirectToTab to="/business/metrics" tab="sku" />}
                    />
                    <Route path="/business/risk" element={admin(<RiskWorkbenchPage />)} />
                    {/* Traffic monitor is an on-call triage tool (its own design
                        doc says "not business reporting"), so it sits in the
                        system nav group (#1007); the route keeps its historical
                        `/business/traffic` path for bookmark stability. */}
                    <Route path="/business/traffic" element={admin(<TrafficMonitorPage />)} />
                    <Route path="/overview" element={<RedirectTo to="/business/overview" />} />
                    {/* Retargeted directly at the hub tab (not the
                        /business/supply shim) to avoid a double redirect hop. */}
                    <Route
                      path="/overview/route-health"
                      element={<RedirectToTab to="/business/metrics" tab="sku" />}
                    />
                    <Route path="/customers/accounts" element={admin(<AccountsList />)} />
                    <Route path="/customers/accounts/:accountId" element={admin(<AccountDetail />)} />
                    <Route path="/customers/ownership" element={customerOwnership(<CustomerOwnershipPage />)} />
                    <Route
                      path="/customers/usage"
                      element={
                        <RedirectToTab
                          to="/business/metrics"
                          tab="usage"
                          renameParams={{ accountId: 'usageAccountId', email: 'usageEmail' }}
                        />
                      }
                    />
                    <Route path="/customers/requests" element={admin(<OpsRequestsPage />)} />
                    <Route path="/customers/billing-anomalies" element={admin(<BillingAnomalies />)} />
                    {/* Customer balance alerts (#1892). Admin-only, same-source with
                        the OPS_UI_ROUTE_PERMISSIONS matrix entry and the nav item —
                        the page holds the global switch for a channel that mails
                        paying customers, and lists their addresses next to their
                        balances. */}
                    <Route path="/customers/balance-alerts" element={admin(<BalanceAlertsPage />)} />
                    {/* Codex at-capacity alerts (#2575). Admin-only, same-source
                        with the OPS_UI_ROUTE_PERMISSIONS matrix entry and the
                        nav item — the switch decides whether ANY Codex
                        capacity mail goes out, test-send is a real
                        unrecallable delivery, a mute silences a named
                        customer, and preview lists customer emails. */}
                    <Route
                      path="/customers/codex-capacity-alerts"
                      element={admin(<CodexCapacityAlertsPage />)}
                    />
                    {/* Operator-authored customer mail (#1911). Admin-only,
                        same-source with the OPS_UI_ROUTE_PERMISSIONS matrix entry
                        and the nav item — one press here is an unrecallable bulk
                        mail, and its second tab edits the copy an unattended cron
                        sends. It sits beside the balance alert because both put a
                        message in a paying customer's inbox; neither has anything
                        to do with upstream supply. */}
                    <Route path="/customers/email-notices" element={admin(<EmailNoticesPage />)} />
                    <Route path="/support/requests" element={support(<SupportRequests />)} />
                    <Route path="/support/tickets" element={support(<SupportTickets />)} />
                    <Route path="/support/announcements" element={admin(<Announcements />)} />
                    <Route
                      path="/finance/revenue"
                      element={<RedirectToTab to="/business/metrics" tab="pnl" />}
                    />
                    <Route path="/finance/receipts" element={receiptsView(<ReceiptsPage />)} />
                    <Route
                      path="/finance/commission-settlement"
                      element={commissionSettlement(<CommissionSettlementPage />)}
                    />
                    <Route path="/finance/payment-orders" element={admin(<PaymentsOrdersPage />)} />
                    <Route path="/finance/payment-providers" element={admin(<PaymentProvidersPage />)} />
                    <Route path="/finance/gift-cards" element={admin(<GiftCards />)} />
                    <Route path="/finance/credit-settings" element={admin(<CreditSettingsPage />)} />
                    <Route
                      path="/finance/credit-overdue"
                      element={customer360(<CreditOverduePage />)}
                    />
                    <Route
                      path="/finance/credit-grants"
                      element={customer360(<CreditGrantHistoryPage />)}
                    />
                    <Route
                      path="/finance/billing-statements"
                      element={customer360(<BillingStatementsPage />)}
                    />
                    <Route path="/finance/fx-policy" element={admin(<PricingPage />)} />
                    <Route path="/finance/reports" element={admin(<ReportsPage />)} />
                    <Route path="/supply/upstreams" element={admin(<UpstreamsList />)} />
                    <Route path="/supply/upstreams/new" element={admin(<NewUpstreamWizard />)} />
                    <Route path="/supply/upstreams/:id" element={admin(<UpstreamDetail />)} />
                    <Route path="/supply/egress-profiles" element={admin(<EgressProfilesPage />)} />
                    <Route path="/supply/egress-lines/:nodeId" element={admin(<EgressLineNodePage />)} />
                    <Route path="/supply/egress-lines" element={admin(<EgressLinesPage />)} />
                    <Route path="/supply/egress-templates" element={<RedirectTo to="/supply/egress-profiles" />} />
                    <Route path="/supply/firewall-rulesets" element={admin(<FirewallRulesetsPage />)} />
                    <Route
                      path="/supply/firewall-rulesets/:rulesetId"
                      element={admin(<FirewallRulesetDetailPage />)}
                    />
                    <Route path="/supply/tenant-routing" element={admin(<TenantRoutingPage />)} />
                    <Route path="/supply/guaranteed-service" element={admin(<GuaranteedServicePage />)} />
                    <Route path="/quotas" element={admin(<QuotaCenterPage />)} />
                    <Route path="/supply/service-levels" element={serviceLevels(<ServiceLevelsPage />)} />
                    <Route path="/supply/models" element={admin(<ModelsList />)} />
                    <Route path="/supply/models/:vendor/:model" element={admin(<ModelDetail />)} />
                    <Route path="/supply/vendors" element={admin(<VendorsPage />)} />
                    {/* Model Market IA (epic #898 P6, #910): the namespace
                        registry + SKU market replace the channel-centric
                        supply pages. The /supply/channels paths stay as
                        redirect shims so operator bookmarks keep working. */}
                    <Route path="/supply/namespaces" element={admin(<NamespacesPage />)} />
                    <Route path="/supply/namespaces/:id" element={admin(<NamespaceDetailPage />)} />
                    <Route path="/supply/channels" element={<RedirectTo to="/supply/namespaces" />} />
                    <Route
                      path="/supply/channels/:id"
                      element={<RedirectToPrefix from="/supply/channels" to="/supply/namespaces" />}
                    />
                    {/* Supply / marketplace domain split (epic #1327 PR-9):
                        buying upstream capacity and selling it as a SKU are
                        two domains, so the marketplace gets its own URL
                        space. The `/supply/market*` and
                        `/supply/sku-activations` paths stay as redirect
                        shims for operator bookmarks only — since #1354 the
                        platform/gateway readiness generators emit `/market/*`
                        deep links directly. */}
                    <Route path="/market" element={admin(<SkuMarketPage />)} />
                    {/* Literal `/market/model-plans` must sit before the
                        `/:skuId` catch so it is not parsed as a SKU id. */}
                    <Route path="/market/model-plans" element={modelPlans(<ModelPlansPage />)} />
                    <Route path="/market/readiness" element={admin(<SkuActivationReadinessPage />)} />
                    <Route path="/market/default-key-namespaces" element={admin(<DefaultKeyNamespacesPage />)} />
                    <Route path="/market/:skuId" element={admin(<SkuDetailPage />)} />
                    <Route path="/supply/market" element={<RedirectTo to="/market" />} />
                    <Route
                      path="/supply/market/:skuId"
                      element={<RedirectToPrefix from="/supply/market" to="/market" />}
                    />
                    <Route path="/supply/sku-activations" element={<RedirectTo to="/market/readiness" />} />
                    <Route path="/governance/audit" element={admin(<Audit />)} />
                    <Route path="/governance/permissions" element={admin(<OpsPermissionsPage />)} />
                    <Route path="/governance/error-policy" element={admin(<UpstreamErrorPolicyPage />)} />
                    <Route path="/governance/router-client-identities" element={admin(<RouterClientIdentitiesPage />)} />
                    <Route path="/governance/ops-users" element={admin(<OpsUsersPage />)} />
                    <Route path="/system/webhook-events" element={admin(<WebhookEventsPage />)} />
                    <Route path="/system/vps" element={admin(<VpsManagementPage />)} />
                    <Route path="/system/cron-runs" element={admin(<CronRunsPage />)} />
                    <Route path="/system/dr-check" element={admin(<DrCheckPage />)} />
                    <Route path="/system/health-perspective" element={admin(<HealthPerspectivePage />)} />
                    <Route path="/upstreams" element={<RedirectTo to="/supply/upstreams" />} />
                    <Route path="/upstreams/new" element={<RedirectTo to="/supply/upstreams/new" />} />
                    <Route path="/upstreams/:id" element={<RedirectToPrefix from="/upstreams" to="/supply/upstreams" />} />
                    <Route path="/tenant-routing" element={<RedirectTo to="/supply/tenant-routing" />} />
                    <Route path="/egress-profiles" element={<RedirectTo to="/supply/egress-profiles" />} />
                    <Route path="/egress-templates" element={<RedirectTo to="/supply/egress-profiles" />} />
                    <Route path="/models" element={<RedirectTo to="/supply/models" />} />
                    <Route path="/models/:vendor/:model" element={<RedirectToPrefix from="/models" to="/supply/models" />} />
                    <Route path="/gift-cards" element={<RedirectTo to="/finance/gift-cards" />} />
                    <Route path="/accounts" element={<RedirectTo to="/customers/accounts" />} />
                    <Route path="/accounts/:accountId" element={<RedirectToPrefix from="/accounts" to="/customers/accounts" />} />
                    <Route path="/billing-anomalies" element={<RedirectTo to="/customers/billing-anomalies" />} />
                    <Route path="/payments/orders" element={<RedirectTo to="/finance/payment-orders" />} />
                    <Route path="/payment-providers" element={<RedirectTo to="/finance/payment-providers" />} />
                    <Route path="/pricing" element={<RedirectTo to="/finance/fx-policy" />} />
                    <Route path="/channels" element={<RedirectTo to="/supply/namespaces" />} />
                    <Route path="/channels/:id" element={<RedirectToPrefix from="/channels" to="/supply/namespaces" />} />
                    <Route path="/vendors" element={<RedirectTo to="/supply/vendors" />} />
                    <Route
                      path="/revenue"
                      element={<RedirectToTab to="/business/metrics" tab="pnl" />}
                    />
                    <Route path="/reports" element={<RedirectTo to="/finance/reports" />} />
                    <Route path="/webhook-events" element={<RedirectTo to="/system/webhook-events" />} />
                    <Route path="/vps" element={<RedirectTo to="/system/vps" />} />
                    <Route path="/cron-runs" element={<RedirectTo to="/system/cron-runs" />} />
                    <Route path="/ops-users" element={<RedirectTo to="/governance/ops-users" />} />
                    <Route path="/dr-check" element={<RedirectTo to="/system/dr-check" />} />
                    <Route path="/audit" element={<RedirectTo to="/governance/audit" />} />
                    <Route path="/permissions" element={<RedirectTo to="/governance/permissions" />} />
                    <Route path="/error-policy" element={<RedirectTo to="/governance/error-policy" />} />
                    <Route path="/router-client-identities" element={<RedirectTo to="/governance/router-client-identities" />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Shell>
              </RequireAuth>
            }
          />
        </Routes>
      </ToastProvider>
    </SessionProvider>
  );
}

function RequireRouteRoles({
  children,
  requiredRoles,
}: {
  readonly children: JSX.Element;
  readonly requiredRoles: readonly OpsRole[];
}): JSX.Element {
  const { user } = useSession();
  const location = useLocation();
  const roles = user?.roles ?? [];
  if (canAccessRoles(roles, requiredRoles)) {
    return children;
  }
  const fallback = firstAccessiblePath(roles);
  if (fallback === null || fallback === location.pathname) {
    return <Forbidden />;
  }
  return <Navigate to={{ pathname: fallback }} replace />;
}

/**
 * `/approvals` → the role-appropriate tab: approvers land on the queue,
 * everyone else is sent to my-submissions (whose own guard redirects
 * non-sales roles away).
 */
function ApprovalsIndexRedirect(): JSX.Element {
  const { user } = useSession();
  const roles = user?.roles ?? [];
  const to = canAccessRoles(roles, APPROVAL_QUEUE_ROLES) ? '/approvals/queue' : '/approvals/mine';
  return <Navigate to={{ pathname: to }} replace />;
}

function RedirectToDefault(): JSX.Element {
  const { user } = useSession();
  const fallback = firstAccessiblePath(user?.roles ?? []);
  if (fallback === null) {
    return <Forbidden />;
  }
  return <Navigate to={{ pathname: fallback }} replace />;
}

function Forbidden(): JSX.Element {
  return (
    <section className="flex flex-col gap-2" data-testid="ops-forbidden-page">
      <h1 className="m-0 text-title-lg text-paper">403</h1>
      <p className="m-0 text-body text-paper-mid">当前运维角色没有访问这个页面的权限。</p>
    </section>
  );
}

function RedirectTo({ to }: { readonly to: string }): JSX.Element {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
}

/**
 * Redirect shim for the pages the metrics hub absorbed (#1643 S5):
 * preserves the incoming query string but sets/overrides `tab` so the
 * hub lands on the matching tab. Compatible params (`window`, `brand`,
 * `from`/`to`) carry straight over; `renameParams` translates params
 * that changed name in the hub (e.g. the usage drill filters, which
 * moved to `usage`-prefixed names so they cannot collide with hub
 * params). An already-present target name wins over the legacy one.
 */
function RedirectToTab({
  to,
  tab,
  renameParams,
}: {
  readonly to: string;
  readonly tab: string;
  readonly renameParams?: Readonly<Record<string, string>>;
}): JSX.Element {
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  for (const [legacy, target] of Object.entries(renameParams ?? {})) {
    const value = search.get(legacy);
    if (value !== null) {
      search.delete(legacy);
      if (search.get(target) === null) search.set(target, value);
    }
  }
  search.set('tab', tab);
  return <Navigate to={{ pathname: to, search: `?${search.toString()}` }} replace />;
}

function RedirectToPrefix({ from, to }: { readonly from: string; readonly to: string }): JSX.Element {
  const location = useLocation();
  const suffix = location.pathname.startsWith(from) ? location.pathname.slice(from.length) : '';
  return <Navigate to={{ pathname: `${to}${suffix}`, search: location.search }} replace />;
}
